import { all, call, CallEffect, put, select } from 'redux-saga/effects';
import * as _ from 'underscore';

import { selectCatalogingOnSiteEnabled } from '../../../../catalog/selectors/CatalogSelectors';
import { Messages } from '../../../../codegen/Localize';
import { selectFlowsOnSiteEnabled } from '../../../../flow/selectors/FlowSelectors';
import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { IResourceQuery } from '../../../../ts/resources/ResourceQuery';
import { ExploreContentApi_ContentType } from '../../../../ts/ServerApi/types/IContent';
import { IStringValuedFilterClause } from '../../../../ts/ServerApi/types/IFilterClause';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { ContentCountsRequestAction, contentCountsRequestSucceeded } from '../../../actions/spaces/ContentCountsActions';
import { selectContentCountsUpToDate } from '../../../selectors/ContentCountsSelector';
import { selectMixedContentFilterClauses } from '../../../selectors/ContentFilterSelectors';
import { selectSpaceInfo } from '../../../selectors/ContentSelectors';
import { SpaceInfoTypeContentCount, SpaceInfoTypeContentCounts } from '../../../state/ContentCountsState';
import { FilterMap } from '../../../state/ContentExplorerState';
import { SiteSpaceContentType } from '../../../state/SiteSpaceContentTypeState';
import { DataRoleSubspaceName, DatasourceSubspaceName, isDataRoleSpace, isDatasourceSpace, isFavoritesSpace, isProjectSpace, isScheduleSpace, isSearchSpace, isSiteSpace, isSiteTasksSpace, isSiteUserSpace, isWorkbookSpace, ScheduleSubspaceName, SearchSubspaceName, SiteTasksSubspaceName, SiteUserSubspaceName, SpaceInfo, SubspaceName, WorkbookSubspaceName } from '../../../state/SpaceInfo';
import { getFavoritesResourceQueryForContentTypes, getProjectOrSiteResourceQueryForContentTypes, getResourceQuery, getSiteResourceQueryForSiteSpaceContentType } from './MixedContentSagas';

export function* processContentCountRequest(resource: IResourceQuery<any>): any {
  try {
    if (_.isUndefined(resource)) {
      return 0;
    }
    resource = resource.withOrder([]); // ignore any sort order

    const count: number = yield call(fetchCountForContent, resource);
    return count;
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// For the counts, all we need is the "totalCount", so just request zero items
export function fetchCountForContent<T>(resource: IResourceQuery<T>): ng.IPromise<number> | undefined {
  return resource.getCount();
}

export function* processContentCountRequestForFavorites(contentType: ExploreContentApi_ContentType) {
  const resource = yield call(getFavoritesResourceQueryForContentTypes, [contentType]);
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForProject(contentType: ExploreContentApi_ContentType) {
  // The counts for the content types need to look specifically at each content type,
  const resource = yield call(getProjectOrSiteResourceQueryForContentTypes, [contentType]);
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForSite(siteSpaceContentType: SiteSpaceContentType) {
  // The counts for the content types need to look specifically at each site content type,
  const resource = yield call(getSiteResourceQueryForSiteSpaceContentType, siteSpaceContentType);
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForSpaceInfo(spaceInfo: SpaceInfo) {
  const resource = yield call(getResourceQuery, spaceInfo);
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForSearchSpaceInfo(spaceInfo: SpaceInfo) {

  // if the current search term in empty, return 0
  const mixedContentFilter: FilterMap = yield select(selectMixedContentFilterClauses);
  if (_.isEmpty(mixedContentFilter) || !mixedContentFilter.search || (mixedContentFilter.search as IStringValuedFilterClause).value.length === 0) {
    return 0;
  }

  const resource = yield call(getResourceQuery, spaceInfo);
  return yield call(processContentCountRequest, resource);
}

export function* handleContentCountsRequest(action: ContentCountsRequestAction) {
  try {
    const isUpToDate = yield select(selectContentCountsUpToDate);
    if (!isUpToDate) {
      const flowsEnabled: boolean = yield select(selectFlowsOnSiteEnabled);
      const catalogingOnSiteEnabled: boolean = yield select(selectCatalogingOnSiteEnabled);
      const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
      const counts: SpaceInfoTypeContentCounts = yield all(getContentCountsRequests(spaceInfo, flowsEnabled, catalogingOnSiteEnabled));
      yield put(contentCountsRequestSucceeded(spaceInfo.spaceType, counts));
    }
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

function getContentCountsRequests(spaceInfo: SpaceInfo, flowsEnabled: boolean, catalogingOnSiteEnabled: boolean): Partial<Record<ExploreContentApi_ContentType | SubspaceName, CallEffect>> {
  if (isSiteSpace(spaceInfo)) {
    const counts: SpaceInfoTypeContentCount<SiteSpaceContentType, CallEffect> = {
      explore: call(processContentCountRequestForSite, 'explore'),
      project: call(processContentCountRequestForSite, 'project'),
      workbook: call(processContentCountRequestForSite, 'workbook'),
      datasource: call(processContentCountRequestForSite, 'datasource'),
      dataRole: FeatureFlags.nlsemanticsServiceCustomDataRoleEnabled ? call(processContentCountRequestForSite,'dataRole') : call(zero),
      view: call(processContentCountRequestForSite, 'view'),
      flow: flowsEnabled ? call(processContentCountRequestForSite, 'flow') : call(zero),
      dataconnection: call(zero),
      database: call(zero),
      table: call(zero),
      metric: call(zero),
    };
    return counts;
  } else if (isProjectSpace(spaceInfo)) {
    const counts: SpaceInfoTypeContentCount<ExploreContentApi_ContentType, CallEffect> = {
      project: call(processContentCountRequestForProject, 'project'),
      workbook: call(processContentCountRequestForProject, 'workbook'),
      datasource: call(processContentCountRequestForProject, 'datasource'),
      dataRole: FeatureFlags.nlsemanticsServiceCustomDataRoleEnabled ? call(processContentCountRequestForProject,'dataRole') : call(zero),
      view: call(processContentCountRequestForProject, 'view'),
      flow: flowsEnabled ? call(processContentCountRequestForProject, 'flow') : call(zero),
      dataconnection: call(zero),
      database: call(zero),
      table: call(zero),
      metric: call(zero),
    };
    return counts;
  } else if (isFavoritesSpace(spaceInfo)) {
    const counts: SpaceInfoTypeContentCount<ExploreContentApi_ContentType, CallEffect> = {
      project: call(processContentCountRequestForFavorites, 'project'),
      workbook: call(processContentCountRequestForFavorites, 'workbook'),
      datasource: call(processContentCountRequestForFavorites, 'datasource'),
      dataRole: FeatureFlags.nlsemanticsServiceCustomDataRoleEnabled ? call(processContentCountRequestForFavorites, 'dataRole') : call(zero),
      view: call(processContentCountRequestForFavorites, 'view'),
      flow: flowsEnabled ? call(processContentCountRequestForFavorites, 'flow') : call(zero),
      dataconnection: call(zero),
      database: call(zero),
      table: call(zero),
      metric: call(zero),
    };
    return counts;
  } else if (isWorkbookSpace(spaceInfo)) {
    const counts: Record<WorkbookSubspaceName, CallEffect> = {
      datasources: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'datasources'}),
      views: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'views'}),
      subscriptions: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'subscriptions'}),
      dataconnections: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'dataconnections'}),
      extractTasks: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'extractTasks'}),
      lineage: call(zero)
    };
    return counts;
  } else if (isDatasourceSpace(spaceInfo)) {
    const counts: Record<DatasourceSubspaceName, CallEffect> = {
      lineage: call(zero),
      nlp: call(zero),
      connections: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'connections'}),
      extractRefreshes: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'extractRefreshes'}),
      remoteRefreshSchedules: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'remoteRefreshSchedules'}),
      connectedWorkbooks: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'connectedWorkbooks'}),
      askDataUsageAnalytics: call(zero),
    };
    return counts;
  } else if (isSearchSpace(spaceInfo)) {
    const counts: Record<SearchSubspaceName, CallEffect> = {
      views: call(processContentCountRequestForSearchSpaceInfo, {...spaceInfo, subspaceName: 'views'}),
      workbooks: call(processContentCountRequestForSearchSpaceInfo, {...spaceInfo, subspaceName: 'workbooks'}),
      datasources: call(processContentCountRequestForSearchSpaceInfo, {...spaceInfo, subspaceName: 'datasources'}),
      flows: call(processContentCountRequestForSearchSpaceInfo, {...spaceInfo, subspaceName: 'flows'}),
      projects: call(processContentCountRequestForSearchSpaceInfo, {...spaceInfo, subspaceName: 'projects'}),
      tables: catalogingOnSiteEnabled? call(processContentCountRequestForSearchSpaceInfo, {...spaceInfo, subspaceName: 'tables'}) : call(zero),
      databases: catalogingOnSiteEnabled? call(processContentCountRequestForSearchSpaceInfo, {...spaceInfo, subspaceName: 'databases'}) : call(zero),
      dataRoles: FeatureFlags.nlsemanticsServiceCustomDataRoleEnabled? call(processContentCountRequestForSearchSpaceInfo, {...spaceInfo, subspaceName: 'dataRoles'}) : call(zero),
    };
    return counts;
  } else if (isSiteUserSpace(spaceInfo)) {
    const counts: Record<SiteUserSubspaceName, CallEffect> = {
      views: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'views'}),
      workbooks: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'workbooks'}),
      datasources: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'datasources'}),
      flows: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'flows'}),
      projects: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'projects'}),
      alerts: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'alerts'}),
      settings: call(zero),
      subscriptions: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'subscriptions'}),
      dataconnections: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'dataconnections'}),
    };
    return counts;
  } else if (isDataRoleSpace(spaceInfo)) {
    const counts: Record<DataRoleSubspaceName, CallEffect> = {
      definition: call(zero),
      connectedFlows: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'connectedFlows'}),
    };
    return counts;
  } else if (isScheduleSpace(spaceInfo)) {
    const counts: Record<ScheduleSubspaceName, CallEffect> = {
      details: call(zero),
      subscriptions: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'subscriptions'}),
      extractRefreshes: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'extractRefreshes'}),
      flows: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'flows'}),
    };
    return counts;
  } else if (isSiteTasksSpace(spaceInfo)) {
      const counts: Record<SiteTasksSubspaceName, CallEffect> = {
        extractRefreshes: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'extractRefreshes'}),
        runFlowTasks: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'runFlowTasks'}),
        subscriptions: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'subscriptions'}),
        alerts: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'alerts'}),
      };
      return counts;
    }
}

function* zero() {
  return 0;
}
